import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { BASIC_PHONE_DISPLAY_BRANDS } from '@redevicer/shared'
import { adminAPI } from '../../api/client'
import { BRAND_ORDER } from '../../utils/phoneCatalog'
import { comparePhoneModelRowsNewestFirst } from '../../utils/phoneModelSort'
import ManufacturerLogo from '../ManufacturerLogo'
import { useAdminUi } from './AdminUiProvider'
import type { AdminBrandSummary, AdminPhoneModelQuality, AdminPhoneModelRow } from './adminTypes'

type Props = {
  token: string
}

type ModelKind = 'smart' | 'basic'

type FormState = {
  manufacturer: string
  model_name: string
  release_year: string
  storage_gb: string
  suggested_price_eur: string
  description: string
}

const emptyForm = (manufacturer = ''): FormState => ({
  manufacturer,
  model_name: '',
  release_year: '',
  storage_gb: '',
  suggested_price_eur: '',
  description: '',
})

function parseStorageGb(raw: string): number[] {
  return raw
    .split(',')
    .map((part) => parseInt(part.trim(), 10))
    .filter((n) => Number.isFinite(n) && n > 0)
}

function rowToForm(row: AdminPhoneModelRow): FormState {
  return {
    manufacturer: row.manufacturer,
    model_name: row.model_name,
    release_year: row.release_year != null ? String(row.release_year) : '',
    storage_gb: row.storage_gb?.length ? row.storage_gb.join(', ') : '',
    suggested_price_eur: row.suggested_price_eur != null ? String(row.suggested_price_eur) : '',
    description: row.description || '',
  }
}

function categoryForKind(kind: ModelKind): string {
  return kind === 'basic' ? 'basic_phone' : 'phone'
}

function sortBrandSummaries(rows: AdminBrandSummary[], kind: ModelKind): AdminBrandSummary[] {
  const order = kind === 'basic' ? BASIC_PHONE_DISPLAY_BRANDS : BRAND_ORDER
  const rank = new Map(order.map((name, index) => [name, index]))
  return [...rows].sort((a, b) => {
    const ra = rank.get(a.manufacturer) ?? 999
    const rb = rank.get(b.manufacturer) ?? 999
    if (ra !== rb) return ra - rb
    return a.manufacturer.localeCompare(b.manufacturer)
  })
}

export default function AdminPhoneModelsPanel({ token }: Props) {
  const { t } = useTranslation()
  const { toast, confirm } = useAdminUi()
  const [kind, setKind] = useState<ModelKind>('smart')
  const [q, setQ] = useState('')
  const [brand, setBrand] = useState<string | null>(null)
  const [brandSummaries, setBrandSummaries] = useState<AdminBrandSummary[]>([])
  const [items, setItems] = useState<AdminPhoneModelRow[]>([])
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(false)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [quality, setQuality] = useState<AdminPhoneModelQuality | null>(null)
  const [backfillBusy, setBackfillBusy] = useState(false)
  const [backfillResult, setBackfillResult] = useState<string>('')

  const showSearchResults = q.trim().length > 0
  const shouldLoadModels = showSearchResults || brand != null

  const loadBrands = useCallback(() => {
    setBrandsLoading(true)
    setError('')
    adminAPI
      .phoneModelBrands(token, { kind })
      .then((res) => setBrandSummaries((res.data?.items as AdminBrandSummary[]) ?? []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setBrandsLoading(false))
  }, [token, kind])

  const loadModels = useCallback(() => {
    if (!shouldLoadModels) {
      setItems([])
      return
    }
    setModelsLoading(true)
    setError('')
    adminAPI
      .listPhoneModels(token, {
        kind,
        q: showSearchResults ? q.trim() : undefined,
        manufacturer: brand ?? undefined,
        limit: 500,
      })
      .then((res) => setItems((res.data?.items as AdminPhoneModelRow[]) ?? []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setModelsLoading(false))
  }, [token, kind, q, brand, showSearchResults, shouldLoadModels])

  const loadQuality = useCallback(() => {
    adminAPI
      .phoneModelQuality(token)
      .then((res) => setQuality(res.data as AdminPhoneModelQuality))
      .catch(() => {})
  }, [token])

  useEffect(() => {
    loadBrands()
    loadQuality()
  }, [loadBrands, loadQuality])

  async function runBackfill() {
    const ok = await confirm({
      title: t('admin.runBackfill'),
      message: t('admin.backfillConfirm'),
      confirmLabel: t('admin.runBackfill'),
    })
    if (!ok) return
    setBackfillBusy(true)
    setBackfillResult('')
    adminAPI
      .backfillPhoneModels(token, { overwrite: false })
      .then((res) => {
        const body = res.data as {
          scanned: number
          updated: number
          release_year_set: number
          storage_set: number
          price_set: number
        }
        setBackfillResult(
          t('admin.backfillResult', {
            scanned: body.scanned,
            updated: body.updated,
            years: body.release_year_set,
            storage: body.storage_set,
            prices: body.price_set,
          }),
        )
        toast.success(t('admin.toast.backfillDone'))
        loadQuality()
        refreshAfterMutation()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBackfillBusy(false))
  }

  useEffect(() => {
    const tmr = window.setTimeout(loadModels, showSearchResults ? 250 : 0)
    return () => window.clearTimeout(tmr)
  }, [loadModels, showSearchResults])

  useEffect(() => {
    setBrand(null)
    setEditingId(null)
    setCreating(false)
    setItems([])
  }, [kind])

  const brands = useMemo(() => sortBrandSummaries(brandSummaries, kind), [brandSummaries, kind])

  const brandModels = useMemo(() => {
    if (!brand) return []
    return items
      .filter((m) => m.manufacturer === brand)
      .sort((a, b) => comparePhoneModelRowsNewestFirst(a, b, () => 0))
  }, [items, brand])

  function refreshAfterMutation() {
    loadBrands()
    loadModels()
  }

  function startCreate() {
    setCreating(true)
    setEditingId(null)
    setForm(emptyForm(brand ?? ''))
  }

  function startEdit(row: AdminPhoneModelRow) {
    setCreating(false)
    setEditingId(row.id)
    setForm(rowToForm(row))
  }

  function cancelEdit() {
    setCreating(false)
    setEditingId(null)
    setForm(emptyForm(brand ?? ''))
  }

  function buildPayload() {
    const releaseYear = form.release_year.trim()
    const priceRaw = form.suggested_price_eur.trim()
    return {
      manufacturer: form.manufacturer.trim(),
      model_name: form.model_name.trim(),
      category: categoryForKind(kind),
      description: form.description.trim() || null,
      release_year: releaseYear ? parseInt(releaseYear, 10) : null,
      storage_gb: parseStorageGb(form.storage_gb),
      suggested_price_eur: priceRaw ? parseFloat(priceRaw) : null,
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError('')
    const payload = buildPayload()
    const req = editingId
      ? adminAPI.updatePhoneModel(token, editingId, payload)
      : adminAPI.createPhoneModel(token, payload)
    req
      .then(() => {
        cancelEdit()
        refreshAfterMutation()
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false))
  }

  async function handleDelete(row: AdminPhoneModelRow) {
    const ok = await confirm({
      title: t('admin.deleteModel'),
      message: t('admin.deleteModelConfirm'),
      confirmLabel: t('admin.deleteModel'),
      danger: true,
    })
    if (!ok) return
    adminAPI
      .deletePhoneModel(token, row.id)
      .then(() => {
        toast.success(t('admin.toast.modelDeleted'))
        refreshAfterMutation()
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
  }

  const loading = brandsLoading || (shouldLoadModels && modelsLoading)

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.models')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.modelsPickerIntro')}</p>
        </div>
        <button type="button" className="vc-admin-btn vc-admin-btn--primary" onClick={startCreate}>
          {t('admin.createModel')}
        </button>
      </header>

      {quality ? (
        <section className="vc-admin-card vc-admin-card--compact vc-admin-catalog-quality">
          <div className="vc-admin-catalog-quality__head">
            <h3>{t('admin.catalogQuality')}</h3>
            <button
              type="button"
              className="vc-admin-btn vc-admin-btn--primary"
              disabled={backfillBusy}
              onClick={runBackfill}
            >
              {backfillBusy ? t('admin.backfillRunning') : t('admin.runBackfill')}
            </button>
          </div>
          <div className="vc-admin-inline-stats">
            <span>
              {t('admin.missingYear')}: <strong>{quality.missing_release_year}</strong> / {quality.total}
            </span>
            <span>
              {t('admin.missingStorage')}: <strong>{quality.missing_storage}</strong>
            </span>
            <span>
              {t('admin.missingPrice')}: <strong>{quality.missing_suggested_price}</strong>
            </span>
          </div>
          {backfillResult ? <p className="vc-admin-muted">{backfillResult}</p> : null}
        </section>
      ) : null}

      <div className="vc-admin-segment">
        <button
          type="button"
          className={`vc-admin-segment__btn${kind === 'smart' ? ' is-active' : ''}`}
          onClick={() => setKind('smart')}
        >
          {t('admin.modelsSmart')}
        </button>
        <button
          type="button"
          className={`vc-admin-segment__btn${kind === 'basic' ? ' is-active' : ''}`}
          onClick={() => setKind('basic')}
        >
          {t('admin.modelsBasic')}
        </button>
      </div>

      <input
        type="search"
        className="vc-admin-input vc-admin-input--search"
        placeholder={t('admin.searchModels')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {error ? <p className="vc-admin-error">{error}</p> : null}
      {loading && brands.length === 0 && !showSearchResults ? (
        <p className="meta-text">{t('common.loading')}</p>
      ) : null}

      {(creating || editingId != null) && (
        <form className="vc-admin-inline-form" onSubmit={handleSubmit}>
          <h3>{editingId ? t('admin.editModel') : t('admin.createModel')}</h3>
          <div className="vc-admin-inline-form__grid">
            <label className="vc-admin-field">
              <span className="vc-admin-label">{t('admin.manufacturer')}</span>
              <input
                className="vc-admin-input"
                value={form.manufacturer}
                onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                required
              />
            </label>
            <label className="vc-admin-field">
              <span className="vc-admin-label">{t('admin.modelName')}</span>
              <input
                className="vc-admin-input"
                value={form.model_name}
                onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                required
              />
            </label>
            <label className="vc-admin-field">
              <span className="vc-admin-label">{t('admin.releaseYear')}</span>
              <input
                className="vc-admin-input"
                type="number"
                min={1990}
                max={2035}
                value={form.release_year}
                onChange={(e) => setForm({ ...form, release_year: e.target.value })}
              />
            </label>
            <label className="vc-admin-field">
              <span className="vc-admin-label">{t('admin.storageGb')}</span>
              <input
                className="vc-admin-input"
                placeholder="64, 128, 256"
                value={form.storage_gb}
                onChange={(e) => setForm({ ...form, storage_gb: e.target.value })}
              />
            </label>
            <label className="vc-admin-field">
              <span className="vc-admin-label">{t('admin.suggestedPrice')}</span>
              <input
                className="vc-admin-input"
                type="number"
                min={0}
                step={0.01}
                value={form.suggested_price_eur}
                onChange={(e) => setForm({ ...form, suggested_price_eur: e.target.value })}
              />
            </label>
          </div>
          <label className="vc-admin-field">
            <span className="vc-admin-label">{t('admin.description')}</span>
            <textarea
              className="vc-admin-input vc-admin-textarea"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <div className="vc-admin-inline-form__actions">
            <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={cancelEdit}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="vc-admin-btn vc-admin-btn--primary" disabled={saving}>
              {saving ? '…' : t('common.save')}
            </button>
          </div>
        </form>
      )}

      {showSearchResults ? (
        <div className="vc-admin-model-list">
          <p className="vc-admin-model-list__heading">
            {modelsLoading ? t('common.loading') : t('admin.searchResults', { count: items.length })}
          </p>
          {items.map((row) => (
            <div key={row.id} className="vc-admin-model-row">
              <button type="button" className="vc-admin-model-row__main" onClick={() => startEdit(row)}>
                <span className="vc-admin-model-row__name">
                  {row.manufacturer} {row.model_name}
                </span>
                <span className="vc-admin-muted">
                  {row.release_year ?? '—'} · {row.storage_gb?.join(', ') || '—'} · €
                  {row.suggested_price_eur ?? '—'} · {row.listings_count} {t('admin.listingsCount').toLowerCase()}
                </span>
              </button>
              {row.listings_count === 0 ? (
                <button
                  type="button"
                  className="vc-admin-btn vc-admin-btn--danger"
                  onClick={() => handleDelete(row)}
                >
                  {t('admin.deleteModel')}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="vc-admin-model-picker">
          {!brand ? (
            <>
              <p className="vc-admin-model-picker__hint">
                {t('admin.pickBrand')} ({brands.length})
              </p>
              <div
                className={`model-picker__grid model-picker__grid--brands${kind === 'basic' ? ' model-picker__grid--basic-brands' : ''}`}
              >
                {brands.map((row) => (
                  <button
                    key={row.manufacturer}
                    type="button"
                    className="model-picker__brand"
                    onClick={() => setBrand(row.manufacturer)}
                  >
                    <ManufacturerLogo manufacturer={row.manufacturer} size={kind === 'basic' ? 48 : 40} />
                    <span>{row.manufacturer}</span>
                    <span className="vc-admin-muted">{row.models_count}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="vc-admin-model-picker__bar">
                <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={() => setBrand(null)}>
                  ← {t('admin.allBrands')}
                </button>
                <span className="vc-admin-model-picker__brand-title">{brand}</span>
                <button type="button" className="vc-admin-btn vc-admin-btn--primary" onClick={startCreate}>
                  + {t('admin.createModel')}
                </button>
              </div>
              {modelsLoading ? <p className="meta-text">{t('common.loading')}</p> : null}
              <div className="vc-admin-model-list">
                {brandModels.map((row) => (
                  <div key={row.id} className="vc-admin-model-row">
                    <button type="button" className="vc-admin-model-row__main" onClick={() => startEdit(row)}>
                      <span className="vc-admin-model-row__name">{row.model_name}</span>
                      <span className="vc-admin-muted">
                        {row.release_year ?? '—'} · {row.storage_gb?.join(', ') || '—'} · €
                        {row.suggested_price_eur ?? '—'}
                      </span>
                    </button>
                    <span className="vc-admin-badge">{row.listings_count}</span>
                    {row.listings_count === 0 ? (
                      <button
                        type="button"
                        className="vc-admin-btn vc-admin-btn--danger"
                        onClick={() => handleDelete(row)}
                      >
                        {t('admin.deleteModel')}
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
